import React, { useState } from "react";
import {
  Card,
  CardHeader,
  CardContent,
  IconButton,
  Typography,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from "@mui/material";
import { Layers as LayersIcon, PlusCircle as PlusCircleIcon } from "lucide-react";
import CourseletItem from "../components/CourseletItem";
import EmptyMessage from "../components/EmptyMessage";
import CourseletForm from "../old/CourseletForm";
import languageLearningApi from "../../middleware/languageLearningApi";

const headerGradient = "linear-gradient(90deg, #1976d2 0%, #2196f3 100%)";

const CourseletsPanel = ({ selectedSectionId, courselets, onRefresh, onSelectCourselet }) => {
  const [showCourseletForm, setShowCourseletForm] = useState(false);
  const [editCourselet, setEditCourselet] = useState(null);
  const [deleteCandidate, setDeleteCandidate] = useState(null);

  const handleAddClick = () => {
    setShowCourseletForm(true);
  };

  const handleFormClose = () => {
    setShowCourseletForm(false);
    setEditCourselet(null);
    if (selectedSectionId) {
      onRefresh(selectedSectionId);
    }
  };

  const handleEditClick = (courselet) => {
    setEditCourselet(courselet);
  };

  const handleDeleteClick = (courselet) => {
    setDeleteCandidate(courselet);
  };

  const handleDeleteConfirm = async () => {
    try {
      await languageLearningApi.delete(`/courselets/${deleteCandidate._id}`);
      setDeleteCandidate(null);
      onRefresh(selectedSectionId);
    } catch (err) {
      console.error("Error deleting courselet:", err);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteCandidate(null);
  };

  // Compute default order for new courselet: last courselet order + 1
  const defaultOrder =
    courselets && courselets.length > 0
      ? Math.max(...courselets.map(c => c.order)) + 1
      : 1;

  // Cascade update: for an edited courselet, update all others whose order is ≥ newOrder.
  const handleCascadeUpdate = async (newOrder, editedCourseletId) => {
    const updates = courselets
      .filter(c => c._id !== editedCourseletId && c.order >= newOrder)
      .map(c => {
        const updatedCourselet = { ...c, order: c.order + 1 };
        return languageLearningApi.put(`/courselets/${c._id}`, updatedCourselet);
      });
    try {
      await Promise.all(updates);
      console.log("Cascade update completed.");
    } catch (err) {
      console.error("Error in cascade update:", err);
    }
  };

  return (
    <>
      <Card 
        elevation={3} 
        sx={{ overflow: 'hidden', height: '100%', opacity: !selectedSectionId ? 0.75 : 1 }}
      >
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <LayersIcon size={20} style={{ marginRight: 8 }} />
              <Typography variant="h6" fontWeight="bold">
                Courselets
              </Typography>
            </Box>
          }
          action={
            selectedSectionId && (
              <IconButton color="primary" onClick={handleAddClick}>
                <PlusCircleIcon size={20} />
              </IconButton>
            )
          }
          sx={{ 
            background: headerGradient, 
            color: 'white',
            '& .MuiCardHeader-action': { color: 'white' }
          }}
        />
        <CardContent sx={{ p: 2 }}>
          {!selectedSectionId ? (
            <EmptyMessage
              message="Select a section to view courselets"
              showAddButton={false}
            />
          ) : (
            <>
              {(showCourseletForm || editCourselet) && (
                <CourseletForm 
                  sectionId={selectedSectionId}
                  courselet={editCourselet}
                  defaultOrder={defaultOrder}
                  onClose={handleFormClose}
                  onCascadeUpdate={handleCascadeUpdate}
                />
              )}
              {courselets && courselets.length > 0 ? (
                courselets.map(courselet => (
                  <CourseletItem 
                    key={courselet._id} 
                    courselet={courselet}
                    onSelectCourselet={onSelectCourselet}
                    onEdit={handleEditClick}
                    onDelete={handleDeleteClick}
                  />
                ))
              ) : (
                <EmptyMessage
                  message="No courselets available"
                  showAddButton
                  onAddClick={handleAddClick}
                />
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={Boolean(deleteCandidate)} onClose={handleDeleteCancel}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete courselet #{deleteCandidate?.order}: "{deleteCandidate?.title}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CourseletsPanel;
